import { FormatterType, FormFieldId, IOlavEntry, ISolrDocList, SearchableFieldId } from '@mwe/models';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { zipCodeValidator } from '../../form/validators/zip-code.validator';

export const searchableFields: SearchableFieldId[] = ['zipCode', 'street', 'building', 'door'];
export const fieldFlow: Record<FormFieldId, FormFieldId | null> = {
  zipCode: 'street',
  location: null,
  street: 'building',
  building: 'door',
  door: null,
};

export const dependencyMap: Record<FormFieldId, FormFieldId | null> = {
  zipCode: 'location',
  location: null,
  street: 'building',
  building: 'door',
  door: null,
};

export const formatterConfig: Record<FormFieldId, Record<FormatterType, (doc: IOlavEntry) => string>> = {
  zipCode: {
    input: doc => doc?.plz ?? '',
    result: doc => `${doc?.plz} ${doc?.gemeinde}`,
  },
  location: {
    input: doc => doc?.gemeinde ?? '',
    result: doc => doc?.gemeinde ?? '',
  },
  street: {
    input: doc => doc?.strasse ?? '',
    result: doc => doc?.strasse ?? '',
  },
  building: {
    input: doc => doc?.gebadr1 ?? '',
    result: doc => doc?.gebadr1 ?? '',
  },
  door: {
    input: doc => doc?.tuerNr ?? '',
    result: doc => doc?.tuerNr ?? '',
  },
};

export function isNoResultsMessage(item: ISolrDocList | unknown): item is string | string[] {
  const noResultsMessage = 'Keine Ergebnisse';
  return (
    (typeof item === 'string' && item.includes(noResultsMessage)) ||
    (Array.isArray(item) && typeof item[0] === 'string' && item[0].includes(noResultsMessage))
  );
}

export function formatValue(input: ISolrDocList, type: FormatterType, context: FormFieldId | null): string {
  if (!context) return '';
  if (isNoResultsMessage(input)) return '';

  const doc = input?.docs?.[0];
  if (!doc) return input as unknown as string;

  return formatterConfig[context]?.[type]?.(doc) ?? '';
}

export function addAdditionalPlaceholders(currentValue: string): string {
  let value = currentValue;

  if (value?.length === 2) {
    value += '??';
  }
  if (value?.length === 3) {
    value += '?';
  }
  return value;
}

export function isSameOlavEntry(doc1: IOlavEntry, doc2: IOlavEntry): boolean {
  return (
    doc1.olavKey === doc2.olavKey ||
    (doc1.plz === doc2.plz &&
      doc1.gemeinde === doc2.gemeinde &&
      doc1.strasse === doc2.strasse &&
      doc1.gebadr1 === doc2.gebadr1 &&
      doc1.tuerNr === doc2.tuerNr)
  );
}

export function isValidTypeaheadObject(controlValue: unknown, selectedOlav: IOlavEntry): boolean {
  const value = controlValue as ISolrDocList | undefined;

  if (value?.docs && Array.isArray(value.docs)) {
    const firstDoc = value.docs[0];
    return !!firstDoc && isSameOlavEntry(firstDoc, selectedOlav);
  }

  return false;
}

export function buildTypeaheadValidator({
  fieldId,
  getSelectedOlav,
  isFieldValidated,
  skipDoorValidation = false,
}: {
  fieldId: FormFieldId;
  getSelectedOlav: () => IOlavEntry | null;
  isFieldValidated: (field: FormFieldId) => boolean;
  skipDoorValidation?: boolean;
}): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const controlValue = control.value;

    if (skipDoorValidation && fieldId === 'door' && !controlValue?.trim?.()?.length) {
      return null;
    }
    // If the field hasn't been "validated" via a selection yet,
    // we don't return an error here (handled by getIsInvalid in component)
    // unless it's empty and required (handled by getBaseValidators)
    if (!isFieldValidated(fieldId)) {
      return null;
    }

    const selectedOlav = getSelectedOlav();
    // If we think it's validated but have no selection, or it's a wildcard
    if (!selectedOlav || (typeof controlValue === 'string' && controlValue.includes('*'))) {
      return { typeaheadMismatch: true };
    }

    // Check if control value is the original ISolrDocList object
    if (isValidTypeaheadObject(controlValue, selectedOlav)) {
      return null;
    }

    // If control value is a string, it must match the selected Olav exactly
    if (typeof controlValue === 'string') {
      const expectedValue = formatterConfig[fieldId]?.input?.(selectedOlav) ?? '';
      if (expectedValue === controlValue && expectedValue !== '') {
        return null;
      }
    }

    return { typeaheadMismatch: true };
  };
}

export function getBaseValidators({
  fieldId,
  isDoorEnabled,
  ignoreAvailableZipCodes = false,
  hasOptionalDoorEntry = false,
}: {
  fieldId: FormFieldId;
  isDoorEnabled: boolean;
  ignoreAvailableZipCodes?: boolean;
  hasOptionalDoorEntry?: boolean;
}): ValidatorFn[] {
  switch (fieldId) {
    case 'zipCode':
      return [Validators.required, Validators.maxLength(4), zipCodeValidator(ignoreAvailableZipCodes)];
    case 'location':
    case 'street':
    case 'building':
      return [Validators.required];
    case 'door':
      return isDoorEnabled && !hasOptionalDoorEntry ? [Validators.required] : [];
    default:
      return [];
  }
}

export function shouldEnableDoor(selectedDocList: ISolrDocList): boolean {
  return selectedDocList?.docs?.some(doc => doc?.tuerNr);
}
